/**
 * Multi-program selection rules for the report filters (SIMS-7990).
 *
 * Kept as pure functions rather than living inside ReportFilters so the AC
 * behaviour is pinned by unit tests and reused by both multi-program reports.
 *
 * The server is the authority on which programs a user may see — the assigned
 * list arrives already narrowed by `user.programs`. These helpers shape the
 * selection UX on top of it; they are not an access control.
 */

/** A `userInfo.programs` entry. `orgUnitId` may hold ids or `{ orgUnitId }` wrappers. */
export interface UserProgramRef {
	programId?: string;
	orgUnitId?: Array<string | { orgUnitId?: string }>;
}

/**
 * The programs the user is assigned to (AC1 — only these are ever listed).
 * Entries without a programId are skipped rather than surfaced as blank options.
 */
export function assignedProgramIds(
	userPrograms: UserProgramRef[] | undefined | null,
): string[] {
	if (!userPrograms) return [];
	return userPrograms
		.map((program) => program.programId?.trim() ?? "")
		.filter((programId) => programId.length > 0);
}

/**
 * The selection the report opens with: **every** assigned program (AC2).
 *
 * Note this differs from today's behaviour, which falls back to the first
 * program only. Pre-selecting must not trigger a run — the Run report action is
 * the only trigger (SIMS-8023 AC1).
 */
export function defaultProgramSelection(
	userPrograms: UserProgramRef[] | undefined | null,
): string[] {
	return assignedProgramIds(userPrograms);
}

/** Add a program to the selection, ignoring a duplicate. Returns a new array. */
export function addProgram(selection: string[], programId: string): string[] {
	if (selection.includes(programId)) return [...selection];
	return [...selection, programId];
}

/**
 * Remove a program — how a chip is dismissed (AC1).
 *
 * Removing the last one is deliberately allowed: the selection may reach empty
 * (AC3), which surfaces the "Select a program" prompt rather than being blocked
 * or auto-restored. Returns a new array.
 */
export function removeProgram(
	selection: string[],
	programId: string,
): string[] {
	return selection.filter((selected) => selected !== programId);
}

/**
 * Whether the report can run (AC3, and the same guard as SIMS-8023 AC5).
 * An empty selection disables Run and clears any previous results.
 */
export function canRunReport(selection: string[]): boolean {
	return selection.length > 0;
}

/**
 * The org units available across the selected programs — the combined set, with
 * a shared org unit appearing ONCE (SIMS-7994 AC1/AC2, FR-2/FR-4).
 *
 * An org unit belonging to several selected programs is one option, not one per
 * program: without de-duplication the filter repeats it and the same id gets sent
 * multiple times in the query.
 *
 * Only the caller's own entitled org units are ever included, since the source is
 * `userInfo.programs`. First-seen order is preserved so the list is stable as the
 * program selection grows.
 */
export function combinedOrgUnitIds(
	userPrograms: UserProgramRef[] | undefined | null,
	selectedProgramIds: string[],
): string[] {
	if (!userPrograms) return [];

	const seen = new Set<string>();
	const combined: string[] = [];

	for (const program of userPrograms) {
		if (!program.programId || !selectedProgramIds.includes(program.programId)) {
			continue;
		}
		for (const ref of program.orgUnitId ?? []) {
			const orgUnitId = typeof ref === "string" ? ref : ref?.orgUnitId;
			const trimmed = orgUnitId?.trim();
			if (trimmed && !seen.has(trimmed)) {
				seen.add(trimmed);
				combined.push(trimmed);
			}
		}
	}

	return combined;
}

/** An org unit shared by more than one selected program. */
export interface SharedOrgUnit {
	orgUnitId: string;
	/** How many of the SELECTED programs include it — the "in N programs" count. */
	programCount: number;
}

/** Org units for the filter, grouped the way the hand-off dropdown shows them. */
export interface OrgUnitGroups {
	/** Shown first, under "Shared across programs", each with its count. */
	shared: SharedOrgUnit[];
	/** Then one group per selected program, in selection order. */
	byProgram: Array<{ programId: string; orgUnitIds: string[] }>;
}

/**
 * Group the entitled org units for the current program selection (SIMS-7994 AC2,
 * FR-4) — the data behind the hand-off's "Org unit dropdown — grouped by program,
 * shared group first".
 *
 * An org unit in **more than one selected program** is shared: it appears once at
 * the top with an "in N programs" count, never again under an individual program.
 * Everything else is listed under the program it belongs to.
 *
 * The count is over the **selected** programs, not every program the org unit
 * belongs to — an org unit shared account-wide but with only one program selected is
 * exclusive to that program *for this report*, and calling it "shared" would be a
 * lie about the current filter.
 */
export function groupOrgUnitsByProgram(
	userPrograms: UserProgramRef[] | undefined | null,
	selectedProgramIds: string[],
): OrgUnitGroups {
	if (!userPrograms || selectedProgramIds.length === 0) {
		return { shared: [], byProgram: [] };
	}

	// Selected programs, in the order they were selected, with their org units.
	const selected = selectedProgramIds
		.map((programId) => ({
			programId,
			orgUnitIds: combinedOrgUnitIds(userPrograms, [programId]),
		}))
		.filter((entry) => entry.orgUnitIds.length > 0);

	const countByOrgUnit = new Map<string, number>();
	for (const { orgUnitIds } of selected) {
		for (const orgUnitId of orgUnitIds) {
			countByOrgUnit.set(orgUnitId, (countByOrgUnit.get(orgUnitId) ?? 0) + 1);
		}
	}

	// Shared first, most-shared first, then by first appearance for a stable order.
	const shared: SharedOrgUnit[] = [];
	for (const { orgUnitIds } of selected) {
		for (const orgUnitId of orgUnitIds) {
			const programCount = countByOrgUnit.get(orgUnitId) ?? 0;
			if (programCount > 1 && !shared.some((s) => s.orgUnitId === orgUnitId)) {
				shared.push({ orgUnitId, programCount });
			}
		}
	}
	shared.sort((a, b) => b.programCount - a.programCount);

	const byProgram = selected
		.map(({ programId, orgUnitIds }) => ({
			programId,
			// Shared ones are already listed above — never repeat them here.
			orgUnitIds: orgUnitIds.filter(
				(orgUnitId) => (countByOrgUnit.get(orgUnitId) ?? 0) === 1,
			),
		}))
		.filter((group) => group.orgUnitIds.length > 0);

	return { shared, byProgram };
}

/**
 * What the server actually applied to one selected program, as returned in the
 * report response's `programFilters` (SIMS-8014 / FR-6).
 *
 * Declared structurally here rather than imported from the service so this module
 * stays free of service dependencies, like the rest of these helpers.
 */
export interface EffectiveProgramFilters {
	programId: string;
	orgUnitIds: string[];
	unrestricted: boolean;
	notApplied: string[];
	orgUnitCount: number;
	excluded: boolean;
}

/**
 * How many selected programs the report actually hid (SIMS-8006 AC1 / FR-5).
 *
 * Uses the server's `excluded` flag when present. The `clientFallback` exists for a
 * frontend running against an API that predates SIMS-8014: it keeps the previous
 * approximate banner rather than silently claiming nothing was hidden, which would
 * be a worse lie than an imprecise count.
 *
 * Note this counts `excluded`, NOT an empty `orgUnitIds`. An `unrestricted` program
 * also has an empty list but is the *widest* scope — keying on emptiness would
 * report the program with the broadest access as hidden.
 */
export function hiddenProgramCount(
	programFilters: EffectiveProgramFilters[] | undefined,
	clientFallback: number,
): number {
	if (!programFilters) return clientFallback;
	return programFilters.filter((program) => program.excluded).length;
}

/**
 * The filters that FR-2 combines across the selected programs but the server applies
 * to every one of them uniformly (SIMS-8890).
 *
 * Names, not ids: the caller already holds the option lists these were picked from,
 * and this module deliberately knows nothing about the services.
 *
 * Provider and the date range are absent on purpose. Provider is single-select
 * (FR-2) and the dates are uniform by nature; both are already stated on the
 * report-level context subtitle, so repeating them per program adds length without
 * adding a fact.
 */
export interface AppliedUniformFilters {
	/** Payer names applied to every selected program. */
	payers?: string[];
	/** Group-label names, same. */
	groupLabels?: string[];
}

/**
 * The per-program context line (SIMS-8014 AC1 / FR-6) — "the filters actually
 * applied to it".
 *
 * Built from the server's effective set, so it is a projection of the query that
 * produced the rows rather than a re-derivation from the client's copy of the
 * entitlements. Returns `null` when there is nothing truthful to say: no data from
 * the server, or a program that was excluded and therefore is not rendered at all.
 *
 * ## Why the payer and group-label segments (SIMS-8890)
 *
 * The format is the Epic Hi-Fi's, transcribed in `ProgramActivitySummaryTab`:
 *
 *     Applied: Statewide Lab Network · … · Payer: Medicaid, Blue Cross
 *
 * The first version shipped the org-unit segment alone, so a line that claimed to
 * list "the filters actually applied" omitted two of the three FR-2 dimensions.
 *
 * ## Why the "not applied" note stays ORG-UNIT ONLY
 *
 * Org units are the only dimension the server narrows per program, so `notApplied`
 * is the only non-applicability it can prove. A payer's could be guessed from
 * `program.payers`, and that guess can contradict the query the line describes: a
 * program whose configuration lists no payer may still hold enrollments carrying
 * that payer's plan schedule. Where a payer genuinely empties a program the server
 * returns it `excluded`, and this function renders nothing for it at all.
 *
 * @param effective      this program's entry from the response's `programFilters`
 * @param orgUnitNameOf  resolves an org-unit id to its display name
 * @param uniform        the payer / group-label filters applied to every program
 */
export function programContextLine(
	effective: EffectiveProgramFilters | undefined,
	orgUnitNameOf: (orgUnitId: string) => string,
	uniform: AppliedUniformFilters = {},
): string | null {
	if (!effective || effective.excluded) return null;

	// Segments, in the Hi-Fi's order. An empty filter contributes nothing rather
	// than an "All" placeholder: this line reports what narrowed the query, and a
	// filter nobody set did not.
	const segments: string[] = [];
	if (uniform.payers?.length) {
		segments.push(`Payer: ${uniform.payers.join(", ")}`);
	}
	if (uniform.groupLabels?.length) {
		segments.push(`Group labels: ${uniform.groupLabels.join(", ")}`);
	}

	// No org-unit entitlement recorded means no org-unit filter was applied to this
	// program, so the honest statement is that all of them are in scope.
	const orgUnits = effective.unrestricted
		? "all org units"
		: effective.orgUnitIds.map(orgUnitNameOf).join(" · ");

	const applied = [orgUnits, ...segments].join(" · ");

	// The note trails the whole line, so the segments read as a list of what applied
	// and the exception is stated once, at the end.
	if (effective.unrestricted || effective.notApplied.length === 0) {
		return `Applied: ${applied}`;
	}

	const skipped = effective.notApplied.map(orgUnitNameOf).join(", ");
	return `Applied: ${applied} — ${skipped} not applied (not in this program)`;
}

/** The dot-separated parts of a section band's context subtitle. */
export interface SectionContextParts {
	/**
	 * The selected program's display name. Rendered ONLY in the combined layout —
	 * see `sectionContextLine` for why.
	 */
	programName?: string;
	/** Org-unit names, already joined. */
	orgUnits?: string;
	/** Payer names, already joined. */
	payers?: string;
	/** The formatted date range, if either bound was set. */
	dateRange?: string;
}

/**
 * The context subtitle under a SECTION BAND — "ENROLLMENT SUMMARY", "INCENTIVE
 * SUMMARY", "VISIT RESULTS" (SIMS-5604, corrected by SIMS-8891).
 *
 * ## Why `perProgram` decides whether the program is named
 *
 * SIMS-5604 wrote this line for a report that ran on ONE program, where naming it
 * under each section title was the only label the reader got. SIMS-8038 then split
 * the report into a section per program, each with its own heading — and the line
 * kept leading with `programName`, which by then meant the FIRST selected program
 * alone. QA read four programs' worth of numbers under a band that named one of them
 * (internal2, 2026-08-17): a scope label that was wrong for three of the four
 * sections beneath it, and redundant for the fourth, whose own heading says the same
 * thing one row below.
 *
 * So the name belongs to the layout that has nowhere else to put it. Where the
 * sections name themselves, the band states only what the reader cannot get from a
 * program heading: the org units, the payers and the date range, none of which the
 * per-program `Applied:` lines carry a date for.
 *
 * An empty part contributes nothing rather than a placeholder, the same rule
 * `programContextLine` follows — a filter nobody set did not narrow the query.
 *
 * @param parts       the segments, in the order the Hi-Fi renders them
 * @param perProgram  true when the response carries a per-program breakdown, so
 *                    every section already names its own program
 */
export function sectionContextLine(
	parts: SectionContextParts,
	perProgram: boolean,
): string {
	return [
		perProgram ? "" : (parts.programName ?? ""),
		parts.orgUnits ?? "",
		parts.payers ?? "",
		parts.dateRange ?? "",
	]
		.filter((segment) => segment.length > 0)
		.join(" · ");
}

/**
 * Resolve a filter-option id to its display name, preferring the first list
 * (SIMS-8889).
 *
 * Exists because the two lists are not interchangeable. For org units, `primary` is
 * the caller's ENTITLED options — fetched by id, so it names every org unit a
 * context line can mention — while the report response's `availableSites` is derived
 * from the enrollments of the selected programs, and therefore omits an entitled org
 * unit whose only enrollments live in a program that was not selected. Resolving
 * against that list alone printed a bare ObjectId on the line.
 *
 * `fallback` is kept for the case the primary list cannot cover: the client's copy of
 * the entitlements going stale against the server's, which decides what the line
 * names.
 *
 * The id is the last resort, not a blank — a blank would read as an empty filter
 * value, which is a worse thing to show than an unresolved one. A `null` name counts
 * as unresolved, since the option type allows one.
 */
export function optionNameResolver(
	primary: ReadonlyArray<{ id: string; name?: string | null }>,
	fallback: ReadonlyArray<{ id: string; name?: string | null }> = [],
): (id: string) => string {
	const nameIn = (
		options: ReadonlyArray<{ id: string; name?: string | null }>,
		id: string,
	): string | undefined =>
		options.find((option) => option.id === id)?.name ?? undefined;

	return (id: string): string =>
		nameIn(primary, id) ?? nameIn(fallback, id) ?? id;
}

/** Helper text under the Program field. Copy is verbatim from the Epic Hi-Fi. */
export function programSelectionHelperText(
	selectedCount: number,
	assignedCount: number,
): string {
	return `${selectedCount} of ${assignedCount} assigned programs selected. At least one required.`;
}

/**
 * SIMS-8038 AC2 (FR-7) — order the per-program sections alphabetically by DISPLAY
 * NAME, and attach the resolved name so every panel renders the same label.
 *
 * Sorting is the client's job here by design: the response carries `programId` only
 * (the report service has no Program model injected), while this app already has the
 * names from the picker. The SAME sorted array drives all three panels, which is what
 * AC2's "the same order in every section type" requires — ordering per panel would
 * let them drift.
 *
 * A program whose name cannot be resolved keeps its section and sorts by its id
 * rather than disappearing or rendering a blank heading — same fallback as
 * `programContextLine`.
 *
 * Pure and generic over the row so it can be unit-tested without the response type.
 */
export function sortProgramBreakdown<T extends { programId: string }>(
	rows: T[] | undefined,
	programNameOf: (programId: string) => string | undefined,
): Array<T & { programName: string }> {
	return [...(rows ?? [])]
		.map((row) => ({
			...row,
			programName: programNameOf(row.programId) ?? row.programId,
		}))
		.sort((a, b) => a.programName.localeCompare(b.programName));
}

/**
 * SIMS-8056 AC1 (FR-11) — the bucket columns for ONE program's Visit Results table.
 *
 * Columns are the union of that program's events' buckets, taken **by label**. Not by
 * `EventResult.id`: those are configured per event, so two events routinely define
 * distinct ids for the same display — the Hi-Fi's own sample has "Not tested" on both
 * a drug test and an attendance event — and unioning by id would emit two identical
 * columns.
 *
 * Label-union has one failure mode of its own (two genuinely different results sharing
 * a display merge into one column), and it is the lesser error: id-union breaks
 * visibly in the common case, while a shared display is a configuration problem a
 * reader could not tell apart either way.
 *
 * Order is first-appearance across the program's events, so the columns follow the
 * order the events were configured in rather than an arbitrary sort.
 */
export function visitBucketColumns(
	events: ReadonlyArray<{ buckets: ReadonlyArray<{ label: string }> }>,
): string[] {
	const seen = new Set<string>();
	const columns: string[] = [];
	for (const event of events) {
		for (const bucket of event.buckets) {
			if (seen.has(bucket.label)) continue;
			seen.add(bucket.label);
			columns.push(bucket.label);
		}
	}
	return columns;
}

/**
 * SIMS-8056 AC1 — one event's row against the program's column set.
 *
 * Returns `null` for a column the event does not define, which the table renders as an
 * em dash. Deliberately NOT 0: a zero asserts a measurement that returned nothing,
 * where the truth is that the measurement does not exist for this event.
 */
export function visitRowFor(
	event: { buckets: ReadonlyArray<{ label: string; count: number }> },
	columns: readonly string[],
): Array<number | null> {
	const byLabel = new Map(event.buckets.map((b) => [b.label, b.count]));
	return columns.map((label) =>
		byLabel.has(label) ? (byLabel.get(label) as number) : null,
	);
}
